import { Component, Input, OnChanges } from '@angular/core';
 
interface FunnelStage {
  count: number;
  rate: number;
  label?: string;   // optional display label; falls back to a title-cased key
}
 
export interface FunnelData {
  funnel: Record<string, FunnelStage>;
  dropoffs: Record<string, number>;
}
 
interface StageVm {
  key: string;
  label: string;
  count: number;
  rate: number;
  countText: string;
  rateText: string;
  width: number;
  weak: boolean;
}

@Component({
  selector: 'ipm-acquisition-funnel',
  templateUrl: './acquisition-funnel.component.html',
  styleUrls: ['./acquisition-funnel.component.scss']
})
export class AcquisitionFunnelComponent implements OnChanges {
  @Input() data!: FunnelData;
  @Input() title = 'Acquisition Funnel';
  @Input() subtitle = 'User journey from ad to purchase';
  @Input() weakThreshold = 50; // min drop-off % to flag a weak stage
 
  private labelMap: Record<string, string> = { add_to_cart: 'Cart' };
 
  private suggestions: Record<string, string> = {
    seen: "Most served impressions aren't being viewed — check ad placement and viewability.",
    click: "Viewers aren't clicking — test stronger creative or a clearer call-to-action.",
    play: "Few clicks turn into a play — make the preview load faster and more prominent.",
    add_to_cart: "Listeners aren't adding to cart — revisit pricing or the add-to-cart prompt.",
    checkout: "Carts aren't reaching checkout — reduce friction or offer a limited-time discount.",
    purchase: "Checkouts aren't converting — simplify payment and reassure on trust and security.",
  };
 
  stages: StageVm[] = [];
  insight = '';
 
  ngOnChanges(): void {
    if (!this.data?.funnel) return;
 
    const entries = Object.entries(this.data.funnel);
    const drops = Object.values(this.data.dropoffs ?? {});
 
    // weak stage = destination of the largest drop-off above threshold
    let weakIdx = -1;
    let maxDrop = -Infinity;
    drops.forEach((d, i) => {
      if (d > maxDrop) {
        maxDrop = d;
        weakIdx = i + 1;
      }
    });
    if (maxDrop < this.weakThreshold) weakIdx = -1;
 
    this.stages = entries.map(([key, v], i) => ({
      key,
      label: v.label ?? this.labelFor(key),
      count: v.count,
      rate: v.rate,
      countText: this.abbrev(v.count),
      rateText: this.fmtRate(v.rate),
      width: this.widthPct(v.rate),
      weak: i === weakIdx,
    }));
 
    this.insight = '';
    if (weakIdx > 0) {
      const from = this.stages[weakIdx - 1];
      const to = this.stages[weakIdx];
      const tip = this.suggestions[to.key] || 'Review this step for friction and drop-off causes.';
      this.insight =
        `The '${to.label}' stage has a high drop-off rate ` +
        `(${from.rateText} to ${to.rateText}). ${tip}`;
    }
  }
 
  // ----- formatting helpers -----
  private labelFor(k: string): string {
    return this.labelMap[k] || k.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
 
  private abbrev(n: number): string {
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'k';
    return String(n);
  }
 
  private fmtRate(r: number): string {
    if (r <= 0) return '0%';
    const s = r >= 10 ? r.toFixed(0) : r >= 1 ? r.toFixed(1) : r.toFixed(2);
    // strip trailing zeros only in the fractional part (so 100 stays 100, not 1)
    return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') + '%';
  }
 
  // perceptual width (sqrt) so tiny stages stay visible; exact value is in the
  // label. Scaled to ~78% max so the rate label has room on the right.
  private widthPct(r: number): number {
    if (r <= 0) return 4;
    return Math.min(Math.max(Math.sqrt(r / 100) * 78, 7), 82);
  }
}
